import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, CheckCircle, Loader2, Users, Gift, Star, Shield, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { familyTranslations } from '../data/translations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Popular cemeteries for autocomplete
const POPULAR_CEMETERIES = {
  en: [
    'Highgate Cemetery',
    'Brompton Cemetery',
    'Kensal Green Cemetery',
    'Père Lachaise Cemetery',
    'Montparnasse Cemetery',
    'Montmartre Cemetery',
    'Arlington National Cemetery',
    'Forest Lawn Memorial Park',
    'Hollywood Forever Cemetery',
  ],
  fr: [
    'Cimetière du Père Lachaise',
    'Cimetière de Montparnasse',
    'Cimetière de Montmartre',
    'Cimetière du Père Lachaise',
    'Highgate Cemetery',
    'Brompton Cemetery',
    'Kensal Green Cemetery',
  ]
};

const WaitlistSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { lang } = useLanguage();
  const t = familyTranslations[lang].waitlistData;
  const fl = familyTranslations[lang].formLabels;
  const [currentStep, setCurrentStep] = useState(1); // 1 or 2
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    pays: '',
    ville: '',
    cimetiere: '',
    localisation: '',
    role: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [validFields, setValidFields] = useState({});
  const sectionRef = useRef(null);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateField = (name, value) => {
    const errors = { ...fieldErrors };
    const valid = { ...validFields };
    
    if (name === 'email' && value) {
      if (!validateEmail(value)) {
        errors.email = lang === 'fr' ? 'Email invalide' : 'Invalid email';
        delete valid.email;
      } else {
        delete errors.email;
        valid.email = true;
      }
    } else if (name === 'email') {
      delete errors.email;
      delete valid.email;
    }
    
    // Mark other required fields as valid when filled
    if (name === 'name' && value.trim().length > 0) {
      valid.name = true;
    } else if (name === 'name') {
      delete valid.name;
    }
    
    if (name === 'pays' && value) {
      valid.pays = true;
    } else if (name === 'pays') {
      delete valid.pays;
    }
    
    if (name === 'ville' && value.trim().length > 0) {
      valid.ville = true;
    } else if (name === 'ville') {
      delete valid.ville;
    }
    
    setFieldErrors(errors);
    setValidFields(valid);
  };

  const handleFieldChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const canProceedToStep2 = () => {
    return formData.name && formData.email && formData.pays && validateEmail(formData.email);
  };

  const goToStep2 = () => {
    if (canProceedToStep2()) {
      setCurrentStep(2);
      // Smooth scroll to top of form
      if (sectionRef.current) {
        sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation
    if (!validateEmail(formData.email)) {
      setError(lang === 'fr' ? 'Veuillez entrer un email valide' : 'Please enter a valid email');
      return;
    }
    
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/waitlist/family`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      const data = await response.json();
      console.log('Waitlist entry created:', data);

      setSubmitted(true);
      setCurrentStep(1); // Reset to step 1
      setFormData({ name: '', email: '', pays: '', ville: '', cimetiere: '', localisation: '', role: '', message: '' });
      setValidFields({});
    } catch (err) {
      console.error('Submission error:', err);
      setError(lang === 'fr' ? 'Une erreur est survenue. Veuillez réessayer.' : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-xl bg-white/15 border border-white/20 text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B4FE9] focus:border-transparent focus:bg-white/20 transition-all duration-200";

  return (
    <section ref={sectionRef} id="get-started" className="py-24 md:py-32 bg-gradient-to-b from-[#1a1a2e] via-[#1e1b4b] to-[#1a1a2e] relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#5B4FE9]/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-xl mx-auto px-6 lg:px-8">
        {/* Social Proof & Benefits Card */}
        <div
          className={`mb-8 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Social Proof with Avatars */}
          <div className="flex flex-col items-center justify-center gap-3 mb-6">
            {/* Avatar Stack */}
            <div className="flex items-center -space-x-2">
              {[
                { bg: 'bg-blue-500', initial: 'JM' },
                { bg: 'bg-purple-500', initial: 'SA' },
                { bg: 'bg-pink-500', initial: 'MC' },
                { bg: 'bg-emerald-500', initial: 'TK' },
                { bg: 'bg-orange-500', initial: 'LB' },
              ].map((avatar, index) => (
                <div
                  key={index}
                  className={`w-10 h-10 rounded-full ${avatar.bg} border-2 border-[#1a1a2e] flex items-center justify-center text-white text-xs font-semibold shadow-lg`}
                  style={{
                    animation: `fadeIn 0.5s ease-out ${index * 0.1}s backwards`,
                  }}
                >
                  {avatar.initial}
                </div>
              ))}
              <div className="w-10 h-10 rounded-full bg-white/10 border-2 border-[#1a1a2e] flex items-center justify-center text-white text-xs font-semibold shadow-lg">
                +2K
              </div>
            </div>
            
            {/* Social Proof Text */}
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-400" />
              <p className="text-sm text-emerald-400 font-medium">
                {t.socialProof}
              </p>
            </div>
          </div>

          {/* Early Bird Benefits Card */}
          <div className="bg-gradient-to-br from-[#5B4FE9]/20 to-purple-500/10 rounded-2xl p-6 border border-[#5B4FE9]/30 backdrop-blur-sm">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Gift className="w-5 h-5 text-yellow-400" />
              {t.earlyBirdTitle}
            </h3>
            <div className="grid gap-3">
              {t.earlyBirdBenefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Star className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div
          className={`text-center mb-10 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2
            className="text-3xl sm:text-4xl font-bold text-white leading-[1.15] tracking-tight"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t.title}
          </h2>
          <p className="mt-4 text-lg text-gray-400 leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        {submitted ? (
          <div className={`text-center p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white">{fl.thankYou}</h3>
            <p className="mt-2 text-gray-400 text-sm">{fl.thankYouSub}</p>
            <button
              onClick={() => setSubmitted(false)}
              className="mt-6 text-sm text-[#8B83F0] hover:text-white transition-colors duration-200"
            >
              {fl.signUpAnother}
            </button>
          </div>
        ) : (
          <form
            onSubmit={currentStep === 2 ? handleSubmit : (e) => { e.preventDefault(); goToStep2(); }}
            className={`space-y-6 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Progress Indicator */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                  currentStep === 1 ? 'bg-[#5B4FE9] text-white' : 'bg-emerald-500 text-white'
                }`}>
                  {currentStep === 1 ? '1' : <Check className="w-4 h-4" />}
                </div>
                <span className="text-sm text-gray-400">{fl.step} 1</span>
              </div>
              <div className={`h-0.5 w-12 transition-all duration-300 ${
                currentStep === 2 ? 'bg-[#5B4FE9]' : 'bg-white/20'
              }`} />
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                  currentStep === 2 ? 'bg-[#5B4FE9] text-white' : 'bg-white/20 text-gray-500'
                }`}>
                  2
                </div>
                <span className="text-sm text-gray-400">{fl.step} 2</span>
              </div>
            </div>

            {/* Step 1: Essential Information */}
            {currentStep === 1 && (
              <div className="space-y-4 animate-fadeIn">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">
                      {fl.name} <span className="text-red-400">{fl.required}</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        autoComplete="name"
                        value={formData.name}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                        className={inputClass}
                        placeholder={fl.namePlaceholder}
                      />
                      {validFields.name && (
                        <Check className="w-4 h-4 text-emerald-400 absolute right-3 top-3.5" />
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">
                      {fl.email} <span className="text-red-400">{fl.required}</span>
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        required
                        autoComplete="email"
                        value={formData.email}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        className={`${inputClass} ${fieldErrors.email ? 'border-red-400' : ''}`}
                        placeholder={fl.emailPlaceholder}
                      />
                      {validFields.email && (
                        <Check className="w-4 h-4 text-emerald-400 absolute right-3 top-3.5" />
                      )}
                    </div>
                    {fieldErrors.email && (
                      <p className="text-xs text-red-400 mt-1">{fieldErrors.email}</p>
                    )}
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    {fl.country} <span className="text-red-400">{fl.required}</span>
                  </label>
                  <div className="relative">
                    <select
                      required
                      autoComplete="country"
                      value={formData.pays}
                      onChange={(e) => handleFieldChange('pays', e.target.value)}
                      className={`${inputClass} appearance-none`}
                    >
                      <option value="" className="bg-[#1a1a2e]">{fl.select}</option>
                      {fl.countries.map((country) => (
                        <option key={country.value} value={country.value} className="bg-[#1a1a2e]">
                          {country.label}
                        </option>
                      ))}
                    </select>
                    {validFields.pays && (
                      <Check className="w-4 h-4 text-emerald-400 absolute right-10 top-3.5" />
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={goToStep2}
                  disabled={!canProceedToStep2()}
                  className="w-full py-3.5 bg-[#5B4FE9] text-white font-semibold rounded-full hover:bg-[#4A3ED8] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#5B4FE9]"
                >
                  {fl.continue}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Step 2: Additional Details */}
            {currentStep === 2 && (
              <div className="space-y-4 animate-fadeIn">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    {fl.cityPostal} <span className="text-red-400">{fl.required}</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      autoComplete="address-level2 postal-code"
                      value={formData.ville}
                      onChange={(e) => handleFieldChange('ville', e.target.value)}
                      className={inputClass}
                      placeholder={fl.cityPlaceholder}
                    />
                    {validFields.ville && (
                      <Check className="w-4 h-4 text-emerald-400 absolute right-3 top-3.5" />
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">
                      {fl.cemetery} <span className="text-gray-500">{fl.ifKnown}</span>
                    </label>
                    <input
                      type="text"
                      value={formData.cimetiere}
                      onChange={(e) => setFormData({ ...formData, cimetiere: e.target.value })}
                      className={inputClass}
                      placeholder={fl.cemeteryPlaceholder}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">
                      {fl.graveLocation} <span className="text-gray-500">{fl.ifKnown}</span>
                    </label>
                    <input
                      type="text"
                      value={formData.localisation}
                      onChange={(e) => setFormData({ ...formData, localisation: e.target.value })}
                      className={inputClass}
                      placeholder={fl.graveLocationPlaceholder}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    {fl.role} <span className="text-gray-500">{fl.optional}</span>
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className={`${inputClass} appearance-none`}
                  >
                    <option value="" className="bg-[#1a1a2e]">{fl.rolePlaceholder}</option>
                    {t.roleOptions.map((role) => (
                      <option key={role} value={role} className="bg-[#1a1a2e]">
                        {role}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    {fl.wishes} <span className="text-gray-500">{fl.optional}</span>
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={3}
                    className={`${inputClass} resize-none`}
                    placeholder={fl.wishesPlaceholder}
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-400 text-center">{error}</p>
                )}

                {/* Privacy Badge */}
                <div className="flex items-center justify-center gap-2 py-2">
                  <p className="text-xs text-gray-400 text-center max-w-md">
                    {fl.privacyBadge}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="px-6 py-3.5 bg-white/10 text-white font-semibold rounded-full hover:bg-white/15 transition-colors duration-200"
                  >
                    {fl.back}
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 py-3.5 bg-[#5B4FE9] text-white font-semibold rounded-full hover:bg-[#4A3ED8] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {fl.sending}
                      </>
                    ) : (
                      <>
                        {t.ctaText}
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            <p className="text-center text-xs text-gray-500 mt-3">
              {t.disclaimer}
            </p>
          </form>
        )}
      </div>
    </section>
  );
};

export default WaitlistSection;
